import clsx from 'clsx';
import styles from './Button.module.css';
import Spinner from '../Spinner/Spinner';

/**
 * Button component with multiple variants and sizes.
 *
 * @param {Object} props
 * @param {'primary' | 'ghost' | 'danger' | 'warning'} [props.variant='primary'] - Visual style
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - Button size
 * @param {boolean} [props.loading] - Show loading spinner
 * @param {boolean} [props.disabled] - Disable the button
 * @param {boolean} [props.fullWidth] - Take full container width
 * @param {React.ReactNode} [props.icon] - Icon to display before children
 * @param {React.ReactNode} [props.iconRight] - Icon to display after children
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 *
 * @example
 * <Button variant="primary" onClick={handleClick}>Save</Button>
 *
 * @example
 * <Button variant="ghost" size="sm" icon={<PlusIcon />}>Add</Button>
 *
 * @example
 * <Button variant="danger" loading>Deleting...</Button>
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  icon,
  iconRight,
  className,
  children,
  ...props
}) {
  const isDisabled = disabled || loading;

  return (
    <button
      className={clsx(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <Spinner size="sm" className={styles.spinner} />
      ) : (
        <>
          {icon && <span className={styles.icon}>{icon}</span>}
          {children && <span className={styles.label}>{children}</span>}
          {iconRight && <span className={styles.iconRight}>{iconRight}</span>}
        </>
      )}
    </button>
  );
}
